/**
 * TimeClockList — stop/start clocks: start a timer against a matter, pause
 * and resume it as you're pulled between things, and finish it into a real
 * draft time entry when the work is done. The open/paused clocks screen
 * Vanessa asked for on /my-time, 29 Sep 2026.
 *
 * A clock is not a time entry -- POST /time-clocks/{id}/finish is what
 * creates one, via hub.time_clocks.finish_clock -> hub.time_entries.
 * create_time_entry, so `onFinished` (bump the sibling TimeEntryList's
 * refresh key, same pattern MyTime already uses for TimeEntryForm) is how
 * a finished clock shows up in "My entries".
 *
 * Elapsed time is read from the server (GET /time-clocks) and then ticked
 * forward client-side once a second for a live display; every action
 * (pause/resume/finish) refetches so the server's own banked figure is
 * never allowed to drift from what's shown.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import SmartClientMatterSearch from "./SmartClientMatterSearch";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Play, Pause, CheckCircle2, X } from "lucide-react";

const RATE_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "complex_expedite", label: "Expedited" },
];
const COST_TAGS = [
  { value: "attendance_on_client", label: "Attendance on client" },
  { value: "attendance_on_other_side", label: "Attendance on other side" },
  { value: "attendance_on_court", label: "Attendance on court" },
  { value: "work_on_documents", label: "Work on documents" },
  { value: "disclosure", label: "Disclosure" },
  { value: "advocacy", label: "Advocacy" },
];

function roundUpToUnit(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round((Math.ceil(n * 10 - 1e-9) / 10) * 10) / 10;
}

function formatElapsed(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function FinishForm({ clock, onCancel, onSubmit, submitting }) {
  const [narrative, setNarrative] = useState(clock.narrative || "");
  const [rateType, setRateType] = useState(clock.rate_type || "standard");
  const [unbillable, setUnbillable] = useState(!clock.chargeable);
  const [costTag, setCostTag] = useState(clock.cost_tag || "");

  const handleSubmit = () => {
    if (!narrative.trim()) {
      toast.error("A narrative is required to finish a clock.");
      return;
    }
    onSubmit({ narrative: narrative.trim(), rate_type: rateType, chargeable: !unbillable, cost_tag: costTag });
  };

  return (
    <div className="mt-2 p-3 rounded-md bg-white/[0.03] border border-navy-border space-y-2" data-testid={`time-clock-finish-form-${clock.id}`}>
      <div>
        <Label className="text-xs font-medium text-teal">Narrative</Label>
        <Textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={2}
          className="mt-1"
          data-testid={`time-clock-finish-narrative-${clock.id}`}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-1">
          {RATE_TYPES.map((rt) => (
            <button
              key={rt.value}
              type="button"
              onClick={() => setRateType(rt.value)}
              className={`text-xs px-2 py-1 rounded-md border ${
                rateType === rt.value ? "bg-teal-dim/30 border-teal text-teal" : "border-navy-border text-slate-400"
              }`}
              data-testid={`time-clock-finish-rate-${rt.value}-${clock.id}`}
            >
              {rt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={unbillable} onCheckedChange={setUnbillable} data-testid={`time-clock-finish-unbillable-${clock.id}`} />
          <Label className="text-xs text-slate-300">Unbillable</Label>
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-teal">Costs tag (optional, for dispute matters)</Label>
        <div className="flex flex-wrap gap-1 mt-1" data-testid={`time-clock-finish-cost-tag-${clock.id}`}>
          {COST_TAGS.map((ct) => (
            <button
              key={ct.value}
              type="button"
              onClick={() => setCostTag((c) => (c === ct.value ? "" : ct.value))}
              className={`text-[11px] px-2 py-1 rounded-md border ${
                costTag === ct.value ? "bg-teal-dim/30 border-teal text-teal" : "border-navy-border text-slate-400"
              }`}
              data-testid={`time-clock-finish-cost-tag-${ct.value}-${clock.id}`}
            >
              {ct.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="button" size="sm" disabled={submitting} onClick={handleSubmit} data-testid={`time-clock-finish-submit-${clock.id}`}>
          {submitting ? "Logging..." : "Log time"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel} data-testid={`time-clock-finish-cancel-${clock.id}`}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// BM-H4296: every clock action tells the rest of the page (the pop-out pill,
// another list) to re-read the clocks now rather than at its next poll.
export const CLOCKS_CHANGED_EVENT = "time-clocks-changed";
function announceClocksChanged() {
  window.dispatchEvent(new CustomEvent(CLOCKS_CHANGED_EVENT));
}

/**
 * fixedMatterRef (BM-H4296, the matter page Time card): the clock is for this
 * matter only. No matter search, and only this matter's clocks are listed.
 * Rate, unbillable and costs tag are still chosen when the clock is finished.
 */
export default function TimeClockList({ onFinished, fixedMatterRef = "" }) {
  const [clocks, setClocks] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [, forceTick] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [finishingId, setFinishingId] = useState(null);

  const [matterRef, setMatterRef] = useState("");
  const [matterLabel, setMatterLabel] = useState("");
  const [startNarrative, setStartNarrative] = useState("");
  const [startRateType, setStartRateType] = useState("standard");
  const [startUnbillable, setStartUnbillable] = useState(false);
  const [startCostTag, setStartCostTag] = useState("");
  const [starting, setStarting] = useState(false);
  // BM-H3459: the search box keeps its own text, so clearing matterRef
  // after a Start left the old name showing over an empty selection, and
  // the next Start said "Pick a matter first". Bumping this remounts it.
  const [searchKey, setSearchKey] = useState(0);

  const tickRef = useRef(null);

  const fetchClocks = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/time-clocks`, { withCredentials: true });
      const all = resp.data?.clocks || [];
      setClocks(fixedMatterRef ? all.filter((c) => c.matter_ref === fixedMatterRef) : all);
      setFetchedAt(Date.now());
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load your clocks."));
    }
    setLoading(false);
  }, [fixedMatterRef]);

  useEffect(() => { fetchClocks(); }, [fetchClocks]);

  useEffect(() => {
    window.addEventListener(CLOCKS_CHANGED_EVENT, fetchClocks);
    return () => window.removeEventListener(CLOCKS_CHANGED_EVENT, fetchClocks);
  }, [fetchClocks]);

  useEffect(() => {
    tickRef.current = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const liveElapsed = (clock) => {
    if (clock.status !== "running") return clock.elapsed_seconds;
    return clock.elapsed_seconds + (Date.now() - fetchedAt) / 1000;
  };

  const handleMatterSelect = (result) => {
    setMatterRef(result?.matter_reference || "");
    setMatterLabel(result?.label || result?.matter_reference || "");
  };

  const handleStart = async () => {
    const startRef = fixedMatterRef || matterRef;
    if (!startRef) {
      toast.error("Pick a matter first: choose it from the search results, typing the name is not enough.");
      return;
    }
    setStarting(true);
    try {
      await axios.post(
        `${API}/time-clocks/start`,
        {
          matter_ref: startRef,
          narrative: startNarrative.trim(),
          rate_type: startRateType,
          chargeable: !startUnbillable,
          cost_tag: startCostTag,
        },
        { withCredentials: true },
      );
      toast.success("Clock started.");
      setMatterRef("");
      setMatterLabel("");
      setSearchKey((k) => k + 1);
      setStartNarrative("");
      setStartRateType("standard");
      setStartUnbillable(false);
      setStartCostTag("");
      announceClocksChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't start that clock."));
    }
    setStarting(false);
  };

  const handlePause = async (clock) => {
    setBusyId(clock.id);
    try {
      await axios.post(`${API}/time-clocks/${clock.id}/pause`, {}, { withCredentials: true });
      announceClocksChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't pause that clock."));
    }
    setBusyId(null);
  };

  const handleResume = async (clock) => {
    setBusyId(clock.id);
    try {
      await axios.post(`${API}/time-clocks/${clock.id}/resume`, {}, { withCredentials: true });
      announceClocksChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't resume that clock."));
    }
    setBusyId(null);
  };

  const handleDiscard = async (clock) => {
    setBusyId(clock.id);
    try {
      await axios.post(`${API}/time-clocks/${clock.id}/discard`, {}, { withCredentials: true });
      toast.success("Clock discarded.");
      announceClocksChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't discard that clock."));
    }
    setBusyId(null);
  };

  const handleFinishSubmit = async (clock, payload) => {
    setBusyId(clock.id);
    try {
      await axios.post(`${API}/time-clocks/${clock.id}/finish`, payload, { withCredentials: true });
      toast.success("Time logged.");
      setFinishingId(null);
      if (onFinished) onFinished();
      announceClocksChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't finish that clock."));
    }
    setBusyId(null);
  };

  return (
    <div data-testid="time-clocks">
      <div className="flex flex-col gap-2 mb-4">
        {!fixedMatterRef && (
          <SmartClientMatterSearch
            key={searchKey}
            label=""
            placeholder="Search matters to start a clock..."
            mattersOnly
            onSelect={handleMatterSelect}
            initialValue={matterLabel}
          />
        )}
        <Input
          placeholder="What are you working on? (optional)"
          value={startNarrative}
          onChange={(e) => setStartNarrative(e.target.value)}
          data-testid="time-clock-start-narrative"
        />
        {!fixedMatterRef && (<>
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-1">
            {RATE_TYPES.map((rt) => (
              <button
                key={rt.value}
                type="button"
                onClick={() => setStartRateType(rt.value)}
                className={`text-xs px-2 py-1 rounded-md border ${
                  startRateType === rt.value ? "bg-teal-dim/30 border-teal text-teal" : "border-navy-border text-slate-400"
                }`}
                data-testid={`time-clock-start-rate-${rt.value}`}
              >
                {rt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={startUnbillable} onCheckedChange={setStartUnbillable} data-testid="time-clock-start-unbillable" />
            <Label className="text-xs text-slate-300">Unbillable</Label>
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium text-teal">Costs tag (optional, for dispute matters)</Label>
          <div className="flex flex-wrap gap-1 mt-1" data-testid="time-clock-start-cost-tag">
            {COST_TAGS.map((ct) => (
              <button
                key={ct.value}
                type="button"
                onClick={() => setStartCostTag((c) => (c === ct.value ? "" : ct.value))}
                className={`text-[11px] px-2 py-1 rounded-md border ${
                  startCostTag === ct.value ? "bg-teal-dim/30 border-teal text-teal" : "border-navy-border text-slate-400"
                }`}
                data-testid={`time-clock-start-cost-tag-${ct.value}`}
              >
                {ct.label}
              </button>
            ))}
          </div>
        </div>
        </>)}
        <Button type="button" disabled={starting} onClick={handleStart} data-testid="time-clock-start-btn">
          <Play className="w-3.5 h-3.5 mr-1" />
          {starting ? "Starting..." : "Start"}
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-4" data-testid="time-clock-list-loading">Loading...</div>
      ) : clocks.length === 0 ? (
        <div className="text-sm text-slate-500 py-4" data-testid="time-clock-list-empty">
          {fixedMatterRef ? "No clock open on this matter." : "No open clocks."}
        </div>
      ) : (
        <div className="space-y-2">
          {clocks.map((clock) => (
            <div
              key={clock.id}
              className="p-3 rounded-md border border-navy-border bg-white/[0.02]"
              data-testid={`time-clock-row-${clock.id}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-white">{clock.matter_ref}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                        clock.status === "running"
                          ? "text-teal border-teal bg-teal-dim/20"
                          : "text-amber-400 border-amber-700 bg-amber-900/20"
                      }`}
                      data-testid={`time-clock-status-${clock.id}`}
                    >
                      {clock.status === "running" ? "Running" : "Paused"}
                    </span>
                  </div>
                  {clock.narrative && (
                    <div className="text-xs text-slate-400 truncate mt-0.5" title={clock.narrative}>{clock.narrative}</div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-mono text-sm text-white" data-testid={`time-clock-elapsed-${clock.id}`}>
                      {formatElapsed(liveElapsed(clock))}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      ≈{roundUpToUnit(liveElapsed(clock) / 3600).toFixed(1)}h
                    </div>
                  </div>

                  <div className="flex gap-1">
                    {clock.status === "running" ? (
                      <button
                        type="button"
                        onClick={() => handlePause(clock)}
                        disabled={busyId === clock.id}
                        className="text-slate-400 hover:text-amber-400 p-1.5 disabled:opacity-50"
                        title="Pause"
                        data-testid={`time-clock-pause-${clock.id}`}
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleResume(clock)}
                        disabled={busyId === clock.id}
                        className="text-slate-400 hover:text-teal p-1.5 disabled:opacity-50"
                        title="Resume"
                        data-testid={`time-clock-resume-${clock.id}`}
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setFinishingId(finishingId === clock.id ? null : clock.id)}
                      disabled={busyId === clock.id}
                      className="text-slate-400 hover:text-teal p-1.5 disabled:opacity-50"
                      title="Finish"
                      data-testid={`time-clock-finish-open-${clock.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDiscard(clock)}
                      disabled={busyId === clock.id}
                      className="text-slate-400 hover:text-red-400 p-1.5 disabled:opacity-50"
                      title="Discard"
                      data-testid={`time-clock-discard-${clock.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {finishingId === clock.id && (
                <FinishForm
                  clock={clock}
                  submitting={busyId === clock.id}
                  onCancel={() => setFinishingId(null)}
                  onSubmit={(payload) => handleFinishSubmit(clock, payload)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
